import React, { useState, useEffect } from 'react';
import { apiFetch } from '../api';
import { useAuth } from '../contexts/AuthContext';

function formatPhone(value) {
  const digits = value.replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

export default function Account() {
  const { user, logout } = useAuth();
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [profileForm, setProfileForm] = useState({
    address: '', city: '', state: '', zip: '', phone: '', carrier: ''
  });
  const [saving, setSaving] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [profileError, setProfileError] = useState('');
  const [profileSuccess, setProfileSuccess] = useState('');

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const data = await apiFetch('/me');
      setProfileForm({
        address: data.address || '',
        city: data.city || '',
        state: data.state || '',
        zip: data.zip || '',
        phone: data.phone ? formatPhone(data.phone) : '',
        carrier: data.carrier || '',
      });
    } catch (e) {
      // ignore
    }
  };

  const handleProfileSave = async (e) => {
    e.preventDefault();
    setProfileError('');
    setProfileSuccess('');
    setSavingProfile(true);
    try {
      await apiFetch('/me/profile', { method: 'PUT', body: profileForm });
      setProfileSuccess('Profile updated successfully!');
      setTimeout(() => setProfileSuccess(''), 3000);
    } catch (e) {
      setProfileError(e.message);
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (passwordForm.new_password !== passwordForm.confirm_password) {
      setError('New passwords do not match');
      return;
    }
    if (passwordForm.new_password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setSaving(true);
    try {
      await apiFetch('/users/change-password', {
        method: 'PUT',
        body: {
          current_password: passwordForm.current_password,
          new_password: passwordForm.new_password,
        },
      });
      setSuccess('Password changed successfully!');
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">My Account</div>
          <div className="page-subtitle">Manage your account settings</div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-title">Account Information</div>
        <div style={{ display: 'grid', gap: 12, maxWidth: 400 }}>
          <div>
            <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>Name</div>
            <div style={{ fontWeight: 600 }}>{user?.name}</div>
          </div>
          <div>
            <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>Email</div>
            <div>{user?.email}</div>
          </div>
          <div>
            <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>Role</div>
            <span className={`badge badge-${user?.role}`}>{user?.role}</span>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-title">My Information</div>
        <p style={{ color: '#64748b', fontSize: 14, marginBottom: 16 }}>
          Keep your contact information up to date.
        </p>

        {profileError && <div className="alert alert-error">{profileError}</div>}
        {profileSuccess && <div className="alert alert-success">{profileSuccess}</div>}

        <form onSubmit={handleProfileSave}>
          <div style={{ display: 'flex', gap: 12, maxWidth: 500 }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Cell Phone</label>
              <input
                className="form-input"
                value={profileForm.phone}
                onChange={(e) => setProfileForm({ ...profileForm, phone: formatPhone(e.target.value) })}
                placeholder="(555) 123-4567"
              />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Carrier</label>
              <select className="form-select" value={profileForm.carrier} onChange={(e) => setProfileForm({ ...profileForm, carrier: e.target.value })}>
                <option value="">Select Carrier</option>
                <option value="verizon">Verizon</option>
                <option value="att">AT&T</option>
                <option value="tmobile">T-Mobile</option>
                <option value="sprint">Sprint</option>
                <option value="uscellular">US Cellular</option>
                <option value="boost">Boost Mobile</option>
                <option value="cricket">Cricket</option>
                <option value="metro">Metro by T-Mobile</option>
                <option value="googlefi">Google Fi</option>
                <option value="mint">Mint Mobile</option>
                <option value="visible">Visible</option>
                <option value="xfinity">Xfinity Mobile</option>
                <option value="spectrum">Spectrum Mobile</option>
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Street Address</label>
            <input
              className="form-input"
              value={profileForm.address}
              onChange={(e) => setProfileForm({ ...profileForm, address: e.target.value })}
              placeholder="123 Main St"
              style={{ maxWidth: 400 }}
            />
          </div>
          <div style={{ display: 'flex', gap: 12, maxWidth: 400 }}>
            <div className="form-group" style={{ flex: 2 }}>
              <label className="form-label">City</label>
              <input
                className="form-input"
                value={profileForm.city}
                onChange={(e) => setProfileForm({ ...profileForm, city: e.target.value })}
                placeholder="Detroit"
              />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">State</label>
              <input
                className="form-input"
                value={profileForm.state}
                onChange={(e) => setProfileForm({ ...profileForm, state: e.target.value })}
                placeholder="MI"
                maxLength={2}
              />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="form-label">Zip</label>
              <input
                className="form-input"
                value={profileForm.zip}
                onChange={(e) => setProfileForm({ ...profileForm, zip: e.target.value })}
                placeholder="48201"
                maxLength={10}
              />
            </div>
          </div>
          <button className="btn btn-primary" type="submit" disabled={savingProfile}>
            {savingProfile ? 'Saving...' : 'Update Information'}
          </button>
        </form>
      </div>

      <div className="card">
        <div className="card-title">Change Password</div>
        <p style={{ color: '#64748b', fontSize: 14, marginBottom: 20 }}>
          Update your account password.
        </p>

        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        <form onSubmit={handlePasswordChange}>
          <div className="form-group">
            <label className="form-label">Current Password</label>
            <input
              className="form-input"
              type="password"
              value={passwordForm.current_password}
              onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
              required
              style={{ maxWidth: 300 }}
            />
          </div>

          <div className="form-group">
            <label className="form-label">New Password</label>
            <input
              className="form-input"
              type="password"
              value={passwordForm.new_password}
              onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
              required
              minLength={6}
              style={{ maxWidth: 300 }}
            />
            <div className="form-hint">Minimum 6 characters</div>
          </div>

          <div className="form-group">
            <label className="form-label">Confirm New Password</label>
            <input
              className="form-input"
              type="password"
              value={passwordForm.confirm_password}
              onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
              required
              style={{ maxWidth: 300 }}
            />
          </div>

          <button className="btn btn-primary" type="submit" disabled={saving}>
            {saving ? 'Changing...' : 'Change Password'}
          </button>
        </form>
      </div>

      <div className="card" style={{ marginTop: 16 }}>
        <button className="btn btn-danger" type="button" onClick={logout} style={{ width: '100%' }}>
          Sign Out
        </button>
      </div>
    </div>
  );
}
